import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { mapApiJob } from "../../utils/jobFormatters";
import useFetch from "../../useFetch";

const fallbackSearches = ["Designer", "Developer", "Web", "IOS", "PHP", "Senior", "Engineer"];

const AnimatedCounter = ({ value }) => {
  const target = Number(value) || 0;
  const [count, setCount] = useState(0);

  useEffect(() => {
    let frameId;
    let startTime;
    const duration = 1100;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * easedProgress));

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    setCount(0);
    frameId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frameId);
  }, [target]);

  return <>{count.toLocaleString()}</>;
};

const Banner = ({ home, isLoading }) => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const { response: optionsResponse } = useFetch(process.env.REACT_APP_BASE_API_URL + "/jobs/filter-options");
  const locationOptions = optionsResponse?.filters?.locations ?? [];
  const featuredJobs = (home?.featured_jobs ?? []).slice(0, 3).map(mapApiJob);
  const popularSearches = home?.popular_searches?.length ? home.popular_searches : fallbackSearches;
  const activeJobs = home?.stats?.active_jobs ?? 0;
  const resumeCount = home?.stats?.resumes ?? home?.stats?.candidates ?? 0;
  const candidateCount = home?.stats?.candidates ?? 0;

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();

    if (keyword) params.set("keyword", keyword);
    if (location) params.set("location", location);

    navigate(`/jobs?${params.toString()}`);
  };

  return (
    <section className="banner-section-three at-home18">
      <div className="auto-container">
        <div className="row align-items-center">
          <div className="content-column col-lg-7">
            <div className="inner-column">
              <div className="title-box wow fadeInUp">
                <h3>Moving Careers and Companies Forward</h3>
                <div className="text">Search career opportunities from active employers and growing teams.</div>
                <div className="home-hero-counters" aria-label="Epic Jobs platform counts">
                  <div className="home-hero-counter">
                    <strong><AnimatedCounter value={resumeCount} />+</strong>
                    <span>Resumes</span>
                  </div>
                  <div className="home-hero-counter">
                    <strong><AnimatedCounter value={activeJobs} />+</strong>
                    <span>Job Postings</span>
                  </div>
                </div>
              </div>

              <div className="job-search-form-two wow fadeInUp" data-wow-delay="200ms">
                <form onSubmit={submitSearch}>
                  <div className="row">
                    <div className="form-group col-lg-5 col-md-12 col-sm-12">
                      <label className="title">What</label>
                      <span className="icon flaticon-search-1"></span>
                      <input type="text" name="keyword" placeholder="Job title, keywords, or company" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                    </div>
                    <div className="form-group col-lg-4 col-md-12 col-sm-12 location">
                      <label className="title">Where</label>
                      <span className="icon flaticon-map-locator"></span>
                      <input type="text" name="location" list="home-location-options" placeholder="City or remote" value={location} onChange={(e) => setLocation(e.target.value)} />
                      <datalist id="home-location-options">
                        {locationOptions.map((item) => (
                          <option value={item} key={item} />
                        ))}
                      </datalist>
                    </div>
                    <div className="form-group col-lg-3 col-md-12 col-sm-12 btn-box">
                      <button type="submit" className="theme-btn btn-style-one">
                        <span className="btn-title">Find Jobs</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="popular-searches at-home18 mb-5 wow fadeInUp" data-wow-delay="400ms">
                <span className="title">Popular Searches: </span>
                {popularSearches.map((item, index) => (
                  <React.Fragment key={item}>
                    <Link className="dark-color" to={`/jobs?keyword=${encodeURIComponent(item)}`}>{item}</Link>
                    {index < popularSearches.length - 1 ? ", " : ""}
                  </React.Fragment>
                ))}
              </div>

              <div className="bottom-box d-flex align-items-center justify-content-between wow fadeInUp" data-wow-delay="1500ms">
                <div className="count-employers d-flex align-items-center">
                  <span className="title mr20 fz16 fw500">{candidateCount}+ Candidates</span>
                  <img src="/images/resource/multi-peoples.png" alt="Candidates" />
                </div>
                <Link to="/candidate-profile" className="upload-cv upload-resume-cv dark-color fw500">
                  <span className="icon flaticon-file"></span> Upload your Resume / CV
                </Link>
              </div>
            </div>
          </div>

          <div className="image-column col-lg-4 offset-lg-1">
            {isLoading && <h5 className="text-center my-3">Loading Data</h5>}
            {!isLoading && featuredJobs.map((job, index) => (
              <div className={`job-block ${index !== 1 ? "mr-30 ml30" : ""} ${index < 2 ? "mb30" : ""} ${index === 0 ? "mt30" : ""}`} key={job.id}>
                <div className="inner-box">
                  <div className="content">
                    <span className="company-logo">
                      {job.logo ? <img className="rounded-circle w50" src={job.logo} alt={job.company} /> : <span className="company-logo-initials">{job.company.charAt(0)}</span>}
                    </span>
                    <h4><Link to={`/jobs/${job.id}`}>{job.title}</Link></h4>
                    <ul className="job-info">
                      <li><span className="icon flaticon-briefcase"></span> {job.company}</li>
                      <li><span className="icon flaticon-map-locator"></span> {job.location}</li>
                    </ul>
                    <ul className="job-other-info">
                      <li className="required">{job.required}</li>
                      <li className="time">{job.type}</li>
                    </ul>
                    <button type="button" className="bookmark-btn" aria-label="Save job">
                      <span className="flaticon-bookmark"></span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Banner;
